import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { CustomerAccount } from '@shared/models';
import { ColorShades, Palette } from '@shared/models/palette';
import { CustomerAccountService } from '@shared/services/api';
import { isDarkColor } from '@shared/services/util/color-util.service';
import {
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';

interface UploadLogoBrand {
  desktopLogo?: string;
  mobileLogo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemingService {
  private http = inject(HttpClient);
  private customerService = inject(CustomerAccountService);

  private readonly CUSTOMER_URL = '/api/users/customer';
  private readonly USER_URL = '/api/users';

  readonly selectedPalette = signal<Palette | null>(null);
  readonly originalPalette = signal<Palette | null>(null);

  readonly brandLogo = signal<string | null>(null);
  readonly mobileLogo = signal<string | null>(null);

  /** Last saved custom colors, kept so the custom preset pickers can seed from them. */
  readonly customPalette = signal<ColorShades | null>(null);

  readonly code = signal<string>('');

  private palettes$?: Observable<Palette[]>;

  init(): Observable<CustomerAccount | null> {
    return this.customerService.getMe().pipe(
      tap((r) => {
        this.code.set(r?.code ?? '');

        if (r?.palette) {
          const palette = { name: r.activeTheme, colors: r.palette };
          this.applyPaletteToDom(palette);
          this.originalPalette.set(palette);
        }

        this.customPalette.set(r?.customPalette ?? null);
        this.brandLogo.set(r?.logoDesktop ?? null);
        this.mobileLogo.set(r?.logoMobile ?? null);
      }),
      catchError(() => of(null)),
    );
  }

  getSystemPalettes(): Observable<Palette[]> {
    return (this.palettes$ ??= this.http
      .get<Palette[]>(`${this.USER_URL}/palette`)
      .pipe(
        map((r) =>
          r.map((p) => {
            return {
              name: p.name,
              colors: p.colors,
            };
          }),
        ),
        shareReplay(1),
      ));
  }

  setProperty(attr: string, value: string) {
    document.documentElement.style.setProperty(attr, value);
  }

  resetSelectedPalette() {
    this.selectedPalette.set(this.originalPalette());
  }

  updateBrandLogo(payload: UploadLogoBrand): Observable<CustomerAccount> {
    return this.patchCustomer(this.buildLogoPayload(payload));
  }

  updatePalette(): Observable<CustomerAccount> {
    return this.patchCustomer(this.buildPalettePayload());
  }

  updatePaletteAndLogo(payload: UploadLogoBrand): Observable<CustomerAccount> {
    return this.patchCustomer({
      ...this.buildPalettePayload(),
      ...this.buildLogoPayload(payload),
    });
  }

  /** Empty when no palette is selected, so a logo-only save still goes through. */
  private buildPalettePayload(): Record<string, unknown> {
    const palette = this.selectedPalette();

    if (!palette?.colors) {
      return {};
    }

    const { name, colors } = palette;

    return {
      activeTheme: name,
      [name === 'custom' ? 'customPalette' : 'colors']: { ...colors },
    };
  }

  private buildLogoPayload(payload: UploadLogoBrand): Record<string, string> {
    const p: Record<string, string> = {};

    if (payload?.desktopLogo) {
      p['logoDesktop'] = payload.desktopLogo;
    }
    if (payload?.mobileLogo) {
      p['logoMobile'] = payload.mobileLogo;
    }

    return p;
  }

  private patchCustomer(
    payload: Record<string, unknown>,
  ): Observable<CustomerAccount> {
    const code = this.code();

    if (!code) {
      return throwError(
        () => new Error('No customer loaded; the theme cannot be saved.'),
      );
    }

    const savedPalette = 'activeTheme' in payload;

    return this.http
      .patch<CustomerAccount>(`${this.CUSTOMER_URL}/${code}`, payload)
      .pipe(tap((r) => this.applySavedTheme(r, savedPalette)));
  }

  private applySavedTheme(r: CustomerAccount, savedPalette: boolean) {
    if (r?.palette) {
      const newPalette = {
        name: r.activeTheme,
        colors: r.palette,
      };
      this.originalPalette.set(newPalette);

      if (savedPalette) {
        this.applyPaletteToDom(newPalette);
      }
    }

    if (r?.customPalette) {
      this.customPalette.set(r.customPalette);
    }

    this.brandLogo.set(r?.logoDesktop ?? null);
    this.mobileLogo.set(r?.logoMobile ?? null);
  }

  selectPalette(p: Palette) {
    this.selectedPalette.set(p);
  }

  private applyCssVariables(variables: Record<string, string>) {
    Object.entries(variables).forEach(([key, value]) => {
      this.setProperty(key, value);
    });
  }

  applyPaletteToDom(palette: Palette) {
    if (!palette?.colors) {
      return;
    }

    const variables =
      palette.name === 'custom'
        ? this.getCustomPaletteVariables(palette)
        : this.getPresetPaletteVariables(palette);

    this.applyCssVariables(variables);
    this.selectPalette(palette);
  }

  private getCustomPaletteVariables(palette: Palette): Record<string, string> {
    const primary = palette.colors.primary;
    const secondary = palette.colors.secondary;

    return {
      ...this.getPrimaryVariables(primary),
      ...this.getSecondaryVariables(secondary),
    };
  }

  private getPresetPaletteVariables(palette: Palette): Record<string, string> {
    const primary = palette.colors.primary;
    const secondary = palette.colors.secondary;
    const white = 'var(--white)';

    return {
      '--palette-dark-900': primary,
      '--palette-dark-100': secondary,
      //header
      '--header-background': primary,
      '--header-text-color': white,

      //logo
      '--logo-background': primary,
      //menu
      '--sidebar-background': primary,
      '--menu-item-color': white,
      '--sidebar-menu-item-bg-active': white,
      '--sidebar-menu-item-color-inactive': white,

      '--surface-btn-primary-disabled-color': 'var(--gray-400)',

      //list
      '--surface-list-item': primary,

      //dynamic
      '--primary-dynamic-color': primary,
      '--surface-primary-text-color': primary,

      //tag
      '--surface-tag-color': primary,
      '--surface-tag-color-checked': primary,
      //tag
      '--surface-tag-bg': secondary,
      '--surface-tag-bg-checked': secondary,
      '--surface-tag-hover-color': 'var(--white-900)',

      //surface icon
      '--surface-icon-color': primary,
      '--surface-btn-default-with-icon': primary,

      //checkbox
      '--surface-checkbox-bg': primary,
      '--surface-checkbox-color': white,

      //tabs
      '--surface-tabs-header-text-color': primary,

      //pagination
      ' --surface-pagination-border-color': primary,
      ' --surface-pagination-color': primary,

      //user-role
      '--surface-user-role-color': primary,

      //btns
      '--surface-btn-link-color': primary,

      //text
      '--surface-text-primary-color': primary,
    };
  }

  private getPrimaryVariables(primary: string): Record<string, string> {
    const dark = isDarkColor(primary);
    const white = 'var(--white)';
    const black = 'var(--black)';

    return dark
      ? {
          '--palette-dark-900': primary,

          //logo
          '--logo-background': primary,

          //header
          '--header-background': primary,
          '--header-text-color': white,
          '--header-icon-color': white,
          //menu
          '--sidebar-background': primary,

          '--menu-item-color': white,
          '--sidebar-menu-item-bg-active': white,
          '--sidebar-menu-item-color-inactive': white,

          // btn
          '--surface-btn-primary-bg': primary,
          '--surface-btn-primary-disabled-color': 'var(--gray-400)',
          '--surface-btn-primary-color': white,
          //btns
          '--surface-btn-link-color': primary,
          // toggle
          '--primary-dynamic-color': primary,

          //checkbox
          '--surface-checkbox-bg': black,
          '--surface-checkbox-color': black,

          //tabs
          '--surface-tabs-header-text-color': primary,

          //user-role
          '--surface-user-role-color': black,

          //text
          '--surface-text-primary-color': primary,
        }
      : {
          '--palette-dark-900': primary,

          //header
          '--header-background': primary,
          //menu
          '--sidebar-background': primary,

          //logo
          '--logo-background': black,
          // header
          '--header-text-color': black,
          '--header-icon-color': black,
          // menu
          '--menu-item-color': 'var(--black',
          '--sidebar-menu-item-bg-active': black,
          '--sidebar-menu-item-color-inactive': black,

          // btn
          '--surface-btn-primary-bg': primary,
          '--surface-btn-primary-color': black,
          //btns
          '--surface-btn-link-color': black,

          // toggle
          '--primary-dynamic-color': black,

          //checkbox
          '--surface-checkbox-bg': 'var(--white-900)',
          '--surface-checkbox-color': 'var(--white-900)',

          //tabs
          '--surface-tabs-header-text-color': black,

          //pagination
          '--surface-pagination-border-color': black,
          '--surface-pagination-color': black,

          //list-item-with-icon
          '--surface-list-item': black,

          //user-role
          '--surface-user-role-color': black,

          //text
          '--surface-text-primary-color': black,
        };
  }

  private getSecondaryVariables(secondary: string): Record<string, string> {
    const dark = isDarkColor(secondary);
    return dark
      ? {
          '--palette-dark-100': secondary,

          //card text color
          '--surface-text-primary-color': 'var(--black)',
          //tag
          '--surface-tag-color': 'var(--white)',

          //list
          '--surface-list-item-selected': 'var(--white-700)',
          //list
          '--surface-list-item': 'var(--black)',
          //tag
          '--surface-tag-bg': secondary,
          '--surface-tag-bg-checked': 'var(--black-300)',
          '--surface-tag-color-checked': 'var(--white)',
          '--surface-tag-hover-color': 'var(--white-900)',

          //status-tag
          '--surface-status-tag-color': 'var(--white)',

          //surface icon
          '--surface-icon-color': 'var(--black)',
          '--surface-btn-default-with-icon': 'var(--white)',

          //btns
          '--surface-btn-default-color': 'var(--white)',
        }
      : {
          '--palette-dark-100': secondary,
          // '--primary-dynamic-color': 'var(--black)',

          //header

          //list
          '--surface-list-item-selected': 'var(--gray-50)',

          // tag
          '--surface-tag-bg': 'var(--white-500)',
          '--surface-tag-hover-color': 'var(--black-900)',
          '--surface-tag-color-checked': 'var(--black-900)',
          '--surface-tag-color': 'var(--black)',
          '--surface-tag-bg-checked': secondary,

          //status-tag
          '--surface-status-tag-color': 'var(--black)',

          //surface icon
          '--surface-icon-color': 'var(--black)',
          '--surface-btn-default-with-icon': 'var(--black)',

          //btns
          '--surface-btn-default-color': 'var(--black)',
        };
  }

  /** Empty for an unloaded palette, leaving consumers on the inherited theme. */
  getThemeVariables(palette: Palette): Record<string, string> {
    if (!palette?.colors) {
      return {};
    }

    return this.getCustomPaletteVariables(palette);
  }

  applyTheme(palette: Palette) {
    const vars = this.getThemeVariables(palette);

    Object.entries(vars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }
}
